import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';

import { getTenantConfigs } from '@/constants/tenant-configs';

import { useConfirmation } from '@/context';
import { useAppTheme } from '@/context/AppThemeContext';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectTerritories } from '@/redux/countries';

import { Tenant } from '@/types/common';

import { getTenantForCountryCode } from '@/utils/languages';

export function useEmailNotificationDrawer(countryCode?: string): void {
  const { t, language } = useTranslate();
  const theme = useTheme();
  const { theme: appThemeConfig } = useAppTheme();
  const { showConfirmation } = useConfirmation();
  const { territoryByCountryCode } = useAppSelector(selectTerritories);

  const shouldShow = useMemo(() => {
    if (!countryCode) return false;
    const tenant = getTenantForCountryCode(territoryByCountryCode, countryCode);
    return (
      getTenantConfigs(tenant as Tenant)?.shouldShowEmailNotificationPopup ??
      false
    );
  }, [countryCode, territoryByCountryCode]);

  const showEmailNotification = useCallback(() => {
    const boldComponents = {
      bold: (
        <Typography
          variant="body2Bold"
          color={theme.palette.text.primary}
          component="span"
        />
      ),
    };

    showConfirmation({
      showHeader: false,
      containerProps: {
        sx: { width: 560, alignItems: 'flex-start' },
      },
      content: (
        <Stack
          direction="column"
          alignItems="flex-start"
          textAlign="left"
          mb={2}
          width="100%"
        >
          <Box display="flex" justifyContent="center" width="100%" mb={0.5}>
            <Box
              component="img"
              src={appThemeConfig.logo}
              alt="logo"
              sx={{ height: 64, objectFit: 'contain' }}
            />
          </Box>

          <Typography variant="h3">
            {t('login.emailNotification.title')}
          </Typography>

          <Typography variant="body2" color={theme.palette.text.secondary}>
            {t('login.emailNotification.greeting')}
          </Typography>

          <Typography variant="body2" color={theme.palette.text.secondary}>
            <Trans
              lang={language}
              i18nKey="login.emailNotification.body"
              components={boldComponents}
            />
          </Typography>

          <Typography variant="body2" color={theme.palette.text.secondary}>
            {t('login.emailNotification.brandingNote')}
          </Typography>

          <Typography variant="body2" color={theme.palette.text.secondary}>
            <Trans
              lang={language}
              i18nKey="login.emailNotification.spamNote"
              components={boldComponents}
            />
          </Typography>

          <Typography variant="body2" color={theme.palette.text.secondary}>
            {t('login.emailNotification.securityNote')}
          </Typography>
        </Stack>
      ),
      primaryText: t('login.emailNotification.button'),
      onConfirm: () => {},
    });
  }, [showConfirmation, appThemeConfig.logo, t, language, theme]);

  useEffect(() => {
    if (shouldShow) {
      showEmailNotification();
    }
  }, [shouldShow, showEmailNotification]);
}
